import { useCallback, useEffect, useState } from 'react';
import {
  MIN_QUESTIONS_PER_ROUND,
  MAX_QUESTIONS_PER_ROUND,
  type AdminQuizView,
  type AdminRoundView,
  type QuizMediaSummary,
} from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';

export function QuizEditor({
  token,
  quiz,
  onChange,
}: {
  token: string;
  quiz: AdminQuizView;
  onChange: (q: AdminQuizView) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const call = async (path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await api<AdminQuizView>(path, { method, token, body }));
    } catch (err) {
      // Was try/finally with no catch: a failed reorder or a rejected round
      // change did nothing at all and said nothing, which reads as the app
      // ignoring you.
      setError(err instanceof ApiError ? err.message : 'That change did not save.');
    } finally {
      setBusy(false);
    }
  };

  // Frozen while a host is part-way through this quiz. The server refuses these
  // changes anyway; this is so the builder says why instead of throwing an error
  // at somebody who was only trying to fix a typo.
  const frozen = quiz.inSession !== null;
  const locked = busy || frozen;

  const errors = quiz.validation.issues.filter((i) => i.level === 'ERROR');
  const warnings = quiz.validation.issues.filter((i) => i.level === 'WARNING');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        {/* The name is what the room sees on their phones, so it has to be
            fixable after the fact -- "Star Wars Night" beats "Untitled". */}
        <input
          defaultValue={quiz.name}
          key={quiz.id}
          onBlur={(e) => {
            const name = e.target.value.trim();
            if (name && name !== quiz.name) void call(`/api/admin/quizzes/${quiz.id}`, 'PATCH', { name });
          }}
          aria-label="Quiz name"
          className="min-w-0 flex-1 rounded-lg bg-transparent px-2 py-1 text-xl font-bold
            outline-none hover:bg-ink-2 focus:bg-ink-2 focus:ring-1 focus:ring-sky"
        />
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-sm font-semibold ${
            quiz.validation.ready ? 'bg-go/20 text-go' : 'bg-stop/20 text-stop'
          }`}
        >
          {quiz.validation.ready ? 'Ready to run' : `${errors.length} to fix`}
        </span>
        <button
          onClick={() => setDeleting(true)}
          className="shrink-0 text-sm text-stop underline"
        >
          Delete quiz
        </button>
      </div>

      {deleting && (
        <DeleteQuizPanel
          token={token}
          quizId={quiz.id}
          quizName={quiz.name}
          onCancel={() => setDeleting(false)}
        />
      )}

      {error && (
        <p className="rounded-xl bg-stop/15 p-3 text-sm text-stop" role="alert">
          {error}
        </p>
      )}

      {quiz.inSession && (
        <p className="rounded-xl bg-flag/15 p-3 text-sm text-flag" role="status">
          <b>{quiz.inSession.eventName}</b> is playing this quiz right now, so the rounds and
          questions are frozen until it ends. Every phone has already downloaded the quiz, so
          changing it mid-round would leave them holding something else. You can still fix the
          name.
        </p>
      )}

      {/* The same validateQuiz the server enforces with, so this is the verdict,
          not a preview of one. */}
      {(errors.length > 0 || warnings.length > 0) && (
        <ul className="space-y-1 rounded-xl bg-ink-2 p-4 text-sm">
          {errors.map((i, n) => (
            <li key={n} className="text-stop">✗ {i.message}</li>
          ))}
          {warnings.map((i, n) => (
            <li key={n} className="text-flag">! {i.message}</li>
          ))}
        </ul>
      )}

      {quiz.rounds.map((round, index) => (
        <RoundCard
          key={round.id}
          round={round}
          index={index}
          total={quiz.rounds.length}
          busy={locked}
          call={call}
          quizId={quiz.id}
          allRoundIds={quiz.rounds.map((r) => r.id)}
        />
      ))}

      <button
        disabled={locked}
        onClick={() => {
          const title = prompt('Round title (e.g. "Music Intros")');
          if (title) void call(`/api/admin/quizzes/${quiz.id}/rounds`, 'POST', { title });
        }}
        className="min-h-12 w-full rounded-xl border border-dashed border-ink-3 text-muted hover:border-muted"
      >
        + Add round
      </button>

      {quiz.validation.ready && <CreateEvent token={token} quizId={quiz.id} />}
    </div>
  );
}

function RoundCard({
  round,
  index,
  total,
  busy,
  call,
  quizId,
  allRoundIds,
}: {
  round: AdminRoundView;
  index: number;
  total: number;
  busy: boolean;
  call: (path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown) => Promise<void>;
  quizId: string;
  allRoundIds: string[];
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  const count = round.questions.length;
  const sizeOk = count >= MIN_QUESTIONS_PER_ROUND && count <= MAX_QUESTIONS_PER_ROUND;

  /** Drag to reorder. The server writes it in two passes; here we just send the order. */
  async function drop(targetQuestionId: string) {
    if (!dragging || dragging === targetQuestionId) return;
    const ids = round.questions.map((q) => q.question.id);
    const from = ids.indexOf(dragging);
    const to = ids.indexOf(targetQuestionId);
    ids.splice(to, 0, ...ids.splice(from, 1));
    setDragging(null);
    await call(`/api/admin/rounds/${round.id}/reorder`, 'POST', { ids });
  }

  const moveRound = async (delta: number) => {
    const ids = [...allRoundIds];
    const to = index + delta;
    if (to < 0 || to >= ids.length) return;
    ids.splice(to, 0, ...ids.splice(index, 1));
    await call(`/api/admin/quizzes/${quizId}/reorder`, 'POST', { ids });
  };

  return (
    <section className="rounded-xl bg-ink-2 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted">Round {index + 1}</span>
        <input
          defaultValue={round.title}
          disabled={busy}
          onBlur={(e) =>
            e.target.value !== round.title &&
            void call(`/api/admin/rounds/${round.id}`, 'PATCH', { title: e.target.value })
          }
          className="flex-1 min-w-40 rounded-lg bg-ink px-3 py-2 font-semibold outline-none focus:ring-1 focus:ring-sky"
        />
        <label className="flex items-center gap-1.5 text-xs text-muted">
          Points
          <input
            type="number"
            step={1}
            min={1}
            defaultValue={round.pointsMultiplierPct}
            disabled={busy}
            onBlur={(e) =>
              void call(`/api/admin/rounds/${round.id}`, 'PATCH', {
                // Integer percentages only: 200 for double points, never 2.0.
                pointsMultiplierPct: Math.round(Number(e.target.value)),
              })
            }
            className="w-20 rounded-lg bg-ink px-2 py-1.5 tabular-nums outline-none"
          />
          %
        </label>
        <label className="flex items-center gap-1.5 text-xs text-muted">
          Timer
          <input
            type="number"
            min={5}
            placeholder="event"
            defaultValue={round.timeLimitSec ?? ''}
            disabled={busy}
            onBlur={(e) =>
              void call(`/api/admin/rounds/${round.id}`, 'PATCH', {
                timeLimitSec: e.target.value ? Number(e.target.value) : null,
              })
            }
            className="w-20 rounded-lg bg-ink px-2 py-1.5 tabular-nums outline-none"
          />
          s
        </label>
        <span className={`text-xs ${sizeOk ? 'text-muted' : 'text-stop'}`}>
          {count}/{MIN_QUESTIONS_PER_ROUND}&ndash;{MAX_QUESTIONS_PER_ROUND}
        </span>
        <div className="flex gap-1">
          <button disabled={busy || index === 0} onClick={() => void moveRound(-1)}
            className="rounded px-2 py-1 text-muted hover:bg-ink-3 disabled:opacity-30">↑</button>
          <button disabled={busy || index === total - 1} onClick={() => void moveRound(1)}
            className="rounded px-2 py-1 text-muted hover:bg-ink-3 disabled:opacity-30">↓</button>
          <button
            disabled={busy}
            onClick={() =>
              confirm(`Delete round "${round.title}"?`) &&
              void call(`/api/admin/rounds/${round.id}`, 'DELETE')
            }
            className="rounded px-2 py-1 text-stop hover:bg-ink-3"
          >
            ×
          </button>
        </div>
      </div>

      <ol className="mt-3 space-y-1">
        {round.questions.map((rq, i) => (
          <li
            key={rq.roundQuestionId}
            draggable={!busy}
            onDragStart={() => !busy && setDragging(rq.question.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => void drop(rq.question.id)}
            className={`flex cursor-grab items-center gap-3 rounded-lg bg-ink px-3 py-2 text-sm
              ${dragging === rq.question.id ? 'opacity-40' : ''}`}
          >
            <span className="w-5 tabular-nums text-muted">{i + 1}</span>
            <span className="flex-1 truncate">{rq.question.prompt}</span>
            <span className="text-xs text-muted">{rq.question.format === 'FREE_TEXT' ? 'text' : 'choice'}</span>
            <span className="max-w-40 truncate text-xs text-go">{rq.question.correctAnswer}</span>
            <button
              disabled={busy}
              onClick={() =>
                void call(
                  `/api/admin/rounds/${round.id}/questions/${rq.question.id}`,
                  'DELETE',
                )
              }
              className="text-muted hover:text-stop disabled:opacity-30"
            >
              ×
            </button>
          </li>
        ))}
        {count === 0 && (
          <li className="py-3 text-center text-sm text-muted">
            Add questions from the bank on the right.
          </li>
        )}
      </ol>
    </section>
  );
}

type JoinMode = 'TICKET' | 'CODE' | 'OPEN';

const MODES: { mode: JoinMode; label: string; blurb: string }[] = [
  {
    mode: 'TICKET',
    label: 'Paid night',
    blurb: 'One code per player, issued at the door once they have paid.',
  },
  {
    mode: 'CODE',
    label: 'Free, shared code',
    blurb: 'One code on the table admits everybody. Free.',
  },
  {
    mode: 'OPEN',
    label: 'Practice quiz',
    blurb:
      'No code at all -- anyone with the link taps join. Runs whenever no real night is set up.',
  },
];

function CreateEvent({ token, quizId }: { token: string; quizId: string }) {
  const [result, setResult] = useState<{
    joinCode: string | null;
    joinMode: JoinMode;
    staffPins: Record<string, string>;
  } | null>(null);
  const [mode, setMode] = useState<JoinMode>('TICKET');
  const [feePence, setFeePence] = useState(300);
  const [audioMode, setAudioMode] = useState<'HOST' | 'PLAYERS' | null>(null);
  const [media, setMedia] = useState<QuizMediaSummary | null>(null);
  const [name, setName] = useState('Tonight');
  const [when, setWhen] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Only a paid night has a fee; the other two are free by definition.
  const effectiveFee = mode === 'TICKET' ? feePence : 0;

  /**
   * Scan the quiz for media on the way in.
   *
   * Two jobs. It asks the audio question only when there IS audio -- nobody
   * should have to answer it for a quiz of text questions. And it puts the
   * weight of the night in front of the person creating the event, before the
   * door rather than after it.
   */
  useEffect(() => {
    let cancelled = false;
    void api<QuizMediaSummary>(`/api/admin/quizzes/${quizId}/media`, { token })
      .then((m) => !cancelled && setMedia(m))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [quizId, token]);

  if (result) {
    return (
      <div className="rounded-xl bg-go/15 p-4 text-sm">
        <p className="font-semibold text-go">Event created.</p>
        {result.joinCode ? (
          <p className="mt-2">
            Join code <span className="font-mono text-lg">{result.joinCode}</span>
          </p>
        ) : (
          <p className="mt-2 text-muted">
            {result.joinMode === 'OPEN'
              ? 'No join code: players just tap join.'
              : 'Codes are issued per player at the door.'}
          </p>
        )}
        <p className="mt-1">
          Host PIN <b>{result.staffPins.host}</b> &middot; Manager PIN{' '}
          <b>{result.staffPins.manager}</b>
        </p>
        <p className="mt-2 text-muted">
          These PINs are shown once and stored hashed. Write them down now.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl bg-ink-2 p-4">
      <div className="grid gap-2 sm:grid-cols-3">
        {MODES.map((m) => (
          <button
            key={m.mode}
            onClick={() => setMode(m.mode)}
            className={`rounded-lg p-3 text-left text-sm ${
              mode === m.mode ? 'bg-sky text-ink' : 'bg-ink text-muted hover:bg-ink-3'
            }`}
          >
            <span className="block font-semibold">{m.label}</span>
            <span className="mt-1 block text-xs opacity-80">{m.blurb}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-muted">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="ml-2 w-48 rounded-lg bg-ink px-2 py-1.5 outline-none"
          />
        </label>

        <label className="text-sm text-muted">
          When
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="ml-2 rounded-lg bg-ink px-2 py-1.5 outline-none"
          />
          {/* The practice quiz sits open with no date, so this stays optional. */}
          <span className="ml-2 text-xs">optional</span>
        </label>

        {mode === 'TICKET' && (
          <label className="text-sm text-muted">
            Entry fee (pence)
            <input
              type="number"
              min={0}
              step={1}
              value={feePence}
              onChange={(e) => setFeePence(Math.max(0, Math.round(Number(e.target.value))))}
              className="ml-2 w-24 rounded-lg bg-ink px-2 py-1.5 tabular-nums outline-none"
            />
            <span className="ml-2 text-xs">£{(feePence / 100).toFixed(2)} per person</span>
          </label>
        )}

        {media && media.audioCount > 0 && (
          <div className="rounded-xl bg-flag/10 p-3">
            <p className="text-sm font-semibold text-flag">
              This quiz has {media.audioCount} audio question
              {media.audioCount === 1 ? '' : 's'}. How will the room hear them?
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(
                [
                  ['HOST', 'Over a speaker', 'The host plays it. Players get no audio at all.'],
                  ['PLAYERS', 'On each phone', 'Every handset plays its own copy on tap.'],
                ] as const
              ).map(([value, label, hint]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAudioMode(value)}
                  className={`min-w-40 flex-1 rounded-lg p-2 text-left text-xs ${
                    audioMode === value ? 'bg-go text-ink' : 'bg-ink-2 hover:bg-ink-3'
                  }`}
                >
                  <span className="block text-sm font-semibold">{label}</span>
                  <span className="block opacity-80">{hint}</span>
                </button>
              ))}
            </div>
            {/* No default on purpose. Guessing wrong makes a whole round
                unusable, and only the person creating the event knows whether
                the venue has a speaker -- or whether this is being played to
                people at home, where there is no shared room at all. */}
            <p className="mt-2 text-xs text-muted">
              There is no default, because guessing wrong makes the round unusable.
            </p>
          </div>
        )}

        {media && media.bytesPerPhone > 0 && (
          <p className="text-xs text-muted">
            {media.imageCount + media.audioCount + media.videoCount} media question
            {media.imageCount + media.audioCount + media.videoCount === 1 ? '' : 's'} &middot;{' '}
            {(media.bytesPerPhone / 1_000_000).toFixed(1)}MB per phone &middot;{' '}
            <b>{((media.bytesPerPhone * 75) / 1_000_000).toFixed(0)}MB across 75 phones</b>,
            downloaded while people arrive.
          </p>
        )}

        <button
          disabled={!name.trim() || (media !== null && media.audioCount > 0 && audioMode === null)}
          onClick={async () => {
            setError(null);
            try {
              setResult(
                await api('/api/admin/events', {
                  method: 'POST',
                  token,
                  body: {
                    quizId,
                    name: name.trim(),
                    entryFeePence: effectiveFee,
                    joinMode: mode,
                    scheduledFor: when ? new Date(when).toISOString() : null,
                    ...(audioMode ? { audioMode } : {}),
                  },
                }),
              );
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Could not create the event.');
            }
          }}
          className="ml-auto min-h-11 rounded-lg bg-go px-5 font-semibold text-ink disabled:opacity-40"
        >
          Create event
        </button>
      </div>
      {error && <p className="text-sm text-stop">{error}</p>}
    </div>
  );
}

interface QuizEventSummary {
  id: string;
  name: string;
  status: 'SETUP' | 'LIVE' | 'ENDED';
  players: number;
  tickets: number;
  submissions: number;
}

interface DeletionReport {
  name: string;
  events: QuizEventSummary[];
  live: QuizEventSummary | null;
  withHistory: QuizEventSummary[];
  canDelete: boolean;
  needsConfirmation: boolean;
}

function counts(e: QuizEventSummary): string {
  const bits = [
    e.players > 0 ? `${e.players} player${e.players === 1 ? '' : 's'}` : null,
    e.tickets > 0 ? `${e.tickets} ticket${e.tickets === 1 ? '' : 's'}` : null,
    e.submissions > 0 ? `${e.submissions} answer${e.submissions === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return bits.length > 0 ? bits.join(' · ') : 'nothing recorded';
}

/**
 * What stands between this quiz and being deleted, and how to get past it.
 *
 * The old flow was a confirm() followed by an alert() saying the quiz "has been
 * run N times" -- true, useless, and a dead end. It did not say which events,
 * whether anything had actually happened on them, or what to do, so somebody
 * with a test event they made ten minutes ago was simply stuck.
 *
 * Three outcomes, and each one names its own way forward:
 *   LIVE       end that event first, with the button to do it right here
 *   history    type the quiz name back, as deleting an event already requires
 *   otherwise  delete, taking the empty events with it
 */
function DeleteQuizPanel({
  token,
  quizId,
  quizName,
  onCancel,
}: {
  token: string;
  quizId: string;
  quizName: string;
  onCancel: () => void;
}) {
  const [report, setReport] = useState<DeletionReport | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReport(await api<DeletionReport>(`/api/admin/quizzes/${quizId}/deletable`, { token }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not check that.');
    }
  }, [quizId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function endEvent(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/admin/events/${id}/end`, { method: 'POST', token });
      // Re-read rather than assume: ending one event may not be the only thing
      // in the way, and the panel should say so before offering Delete again.
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not end that event.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      const q = report?.needsConfirmation ? `?confirmName=${encodeURIComponent(typed)}` : '';
      await api(`/api/admin/quizzes/${quizId}${q}`, { method: 'DELETE', token });
      location.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that quiz.');
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-stop/40 bg-stop/10 p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold text-stop">Delete &ldquo;{quizName}&rdquo;?</p>
        <button onClick={onCancel} className="text-xs text-muted underline hover:text-chalk">
          Cancel
        </button>
      </div>

      {!report && !error && <p className="mt-2 text-sm text-muted">Checking&hellip;</p>}
      {error && <p className="mt-2 text-sm text-stop">{error}</p>}

      {report && (
        <>
          <p className="mt-2 text-sm">
            The rounds go with it. The questions stay in the bank.
          </p>

          {report.events.length > 0 && (
            <ul className="mt-3 space-y-2">
              {report.events.map((e) => (
                <li
                  key={e.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-ink-2 p-2 text-sm"
                >
                  <span className="min-w-0">
                    <b className="block truncate">{e.name}</b>
                    <span className="text-xs text-muted">
                      {e.status} &middot; {counts(e)}
                    </span>
                  </span>
                  {e.status === 'LIVE' ? (
                    <button
                      disabled={busy}
                      onClick={() => void endEvent(e.id)}
                      className="shrink-0 rounded-lg bg-flag px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-40"
                    >
                      End this event
                    </button>
                  ) : (
                    <span className="shrink-0 text-xs text-muted">
                      {e.players + e.tickets + e.submissions > 0
                        ? 'will be deleted'
                        : 'empty — will be deleted'}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {report.live && (
            <p className="mt-3 text-sm text-flag">
              {/* Not forceable, and that is the point: every phone in the room
                  has downloaded this quiz, and deleting it cascades the event
                  out from under them mid-round. Ending it first is one tap. */}
              <b>{report.live.name}</b> is playing this quiz right now with{' '}
              {report.live.players} player{report.live.players === 1 ? '' : 's'} in. End it first
              &mdash; there is no way past this one, because the room is holding this quiz.
            </p>
          )}

          {report.needsConfirmation && (
            <div className="mt-3">
              <p className="text-sm text-stop">
                This also deletes {report.withHistory.length} night
                {report.withHistory.length === 1 ? '' : 's'} that something happened on, and
                everything attached. There is no undo.
              </p>
              <label className="mt-2 block text-xs text-muted">
                Type the quiz&rsquo;s name to confirm
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={report.name}
                  className="mt-1 w-full rounded-lg bg-ink px-2 py-1.5 text-sm text-chalk outline-none"
                />
              </label>
            </div>
          )}

          <div className="mt-3 flex gap-2">
            <button
              disabled={
                busy || !report.canDelete || (report.needsConfirmation && typed !== report.name)
              }
              onClick={() => void remove()}
              className="rounded-lg bg-stop px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            >
              {report.canDelete ? 'Delete the quiz' : 'Blocked'}
            </button>
            <button
              disabled={busy}
              onClick={() => void load()}
              className="rounded-lg bg-ink-2 px-4 py-2 text-sm hover:bg-ink-3"
            >
              Re-check
            </button>
          </div>
        </>
      )}
    </div>
  );
}
